import { TimeProgramSiteStatus, STProgramSiteStatus } from '@tempus/trials-types';
import { isAxiosError } from 'axios';
import moment from 'moment';
import React from 'react';
import YAML from 'yaml';

import { formatDateForHyphenDisplay, formatDateForSlashDisplay } from '~/utils/date';

const CALENDAR_PARSE_FORMATS = [moment.ISO_8601, 'YYYY-MM-DD', 'MM/DD/YYYY'];

export const formatDateToYearMonthDay = (dateString: string): string => {
  if (!dateString) {
    return '';
  }

  const parsed = moment(dateString, CALENDAR_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

export const formatDateForDisplay = (dateString?: string | Date | null, isUtc?: boolean): string => {
  return formatDateForHyphenDisplay(dateString, isUtc);
};

export const formatDateToMonthDayYear = (dateString: string | Date | null, isUtc?: boolean): string => {
  return formatDateForSlashDisplay(dateString, isUtc);
};

export const sentenceCase = (s: string) => (s && s[0].toUpperCase() + s.slice(1).toLowerCase()) || '';

export const convertSnakeToSentenceCase = (s: string) => {
  return sentenceCase(s.replace(/_/g, ' '));
};

export const convertCamelToSentenceCase = (s: string): string => sentenceCase(s.replace(/([A-Z])/g, ' $1').trim());

export const getCurrentDate = (): string => {
  return formatDateToMonthDayYear(new Date());
};

export const cleanText = (s: string) => {
  return s.replace(/x000D/g, '').replace(/_/g, '').replace(/>\r\n/g, '').replace(/ {2}/g, ' ');
};

function formatApiErrorMessage(message: unknown): string | undefined {
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  if (Array.isArray(message)) {
    const parts = message
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .map((part) => part.trim());

    if (parts.length > 0) {
      return parts.join('; ');
    }
  }

  if (message && typeof message === 'object' && 'message' in message) {
    return formatApiErrorMessage((message as { message: unknown }).message);
  }

  return undefined;
}

export const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const apiMessage = formatApiErrorMessage(error.response?.data?.message);
    if (apiMessage) {
      return apiMessage;
    }
  }

  return error instanceof Error ? error.message : String(error);
};

// Replace line separators within the content so they are recognized as a linebreaks when exported to a CSV
// Copied from trials-ui/src/common/DownloadCSV/format-patients-for-csv.js
export const replaceLineSeparators = (content) => (content ? content.replace(/[\n|\r]/g, '\u2028') : content);

export const safelyParseYAML = (s: string) => {
  return YAML.parse(cleanText(s));
};

export const getBadgeWidth = (s: string) => {
  switch (s) {
    case TimeProgramSiteStatus.MATCHING:
    case STProgramSiteStatus.MATCHING:
      return '114px';
    case TimeProgramSiteStatus.UNDER_SITE_REVIEW:
      return '110px';
    default:
      return 'min-content';
  }
};

/**
 * Validates if a string is a valid UUID format
 * @param uuid - The string to validate
 * @returns true if the string is a valid UUID, false otherwise
 */
export const isValidUUID = (uuid: string): boolean => {
  if (!uuid || typeof uuid !== 'string') {
    return false;
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Mints a RFC 4122 v4 UUID. Always use this helper instead of calling
 * `crypto.randomUUID()` directly.
 *
 * Prefers `crypto.randomUUID()`, falling back to `crypto.getRandomValues()`
 * when it is unavailable. `crypto.randomUUID` is only exposed in [secure
 * contexts](https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts):
 * `https://*`, the literal hostnames `localhost` / `127.0.0.1` / `[::1]`,
 * `file://`, and extensions. Browsers decide this on the hostname string, not
 * on DNS resolution — so `http://local.devtempus.com` (mapped to 127.0.0.1 via
 * /etc/hosts for local dev) is NOT a secure context, `crypto.randomUUID` is
 * undefined there, and any call to it throws "crypto.randomUUID is not a
 * function". `crypto.getRandomValues` has no secure-context restriction and
 * draws from the same CSPRNG, so a v4 UUID synthesized from it is a safe
 * replacement that keeps local dev working across URL conventions.
 *
 * Note: `src/polyfills.ts` installs the same fallback as a global polyfill at
 * app bootstrap so that third-party library code (e.g. `giorules-jdm-editor`,
 * which calls `crypto.randomUUID()` directly in 13+ places) also stays alive.
 * In the running app the primary path above will usually succeed because of
 * that polyfill; this util is still the right thing to call from our own code
 * because it works in environments where the polyfill has not run (standalone
 * scripts, SSR, tests that reset globals).
 */
export const randomUUID = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Clears `document.body` styles commonly applied by modal `preventScroll` or scroll-lock patterns.
 * Use when navigating away before a modal can run its normal `onAfterClose` / unlock (see FormModal/Ruleset, CarePathwayModal)
 * and on unmount of full-page flows (e.g. ruleset editor) that may leave the care pathway project page non-scrollable.
 */
export const resetBodyScrollLockStyles = (): void => {
  document.body.style.overflow = '';
  document.body.style.overflowY = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
};

/** True for a plain primary-button click (not modifier/middle-click new-tab gestures). */
export function isPlainPrimaryClick(
  event: Pick<React.MouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export const preventAnchorClick = (e: React.MouseEvent<HTMLElement>) => {
  if ((e.target as HTMLElement).tagName === 'A' && isPlainPrimaryClick(e)) {
    e.preventDefault();
  }
};

export const getInitials = (name: string = '') => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return '';
  }

  const names = trimmedName.split(/\s+/);
  const initials = names.map((n) => n.charAt(0)).join('');
  return initials;
};
